/**
 * Plan pricing, add-on pricing and monthly recurring revenue.
 *
 * All of these take the settings object `S` explicitly rather than reading a
 * global, so the same functions serve the UI, the AI context builder and any
 * future test without carrying hidden state.
 */
import { periodDays } from '../lib/dates.js';

/** Monthly list price of a plan, 0 for anything unpriced. */
export function priceOf(S, plan) {
  const p = (S.prices || {})[String(plan || '').toUpperCase()];
  return typeof p === 'number' ? p : 0;
}

/* ------------------------------------------------------------- add-ons */

export const moduleList = r =>
  String(r.addons || '').split(';').map(x => x.trim()).filter(Boolean);

export function modulePrice(S, name) {
  const p = (S.modules || {})[name];
  return typeof p === 'number' ? p : 0;
}

/** Some plans bundle every add-on, so they never cost extra. */
export const isBundled = (S, r) =>
  (S.bundlePlans || []).includes(String(r.plan || '').toUpperCase());

export function addonMonthly(S, r) {
  if (isBundled(S, r)) return 0;
  return moduleList(r).reduce((t, m) => t + modulePrice(S, m), 0);
}

/** Attach rate and add-on revenue across the live, paid book. */
export function moduleStats(S, rows, derive) {
  const pool = rows.map(r => ({ r, d: derive(r) }))
    .filter(x => !x.d.excluded && x.d.live && priceOf(S, x.r.plan) > 0);
  const eligible = pool.filter(x => !isBundled(S, x.r));
  const attached = eligible.filter(x => moduleList(x.r).length);
  const revenue = pool.reduce((t, x) => t + addonMonthly(S, x.r), 0);

  const count = {};
  pool.forEach(x => moduleList(x.r).forEach(m => { count[m] = (count[m] || 0) + 1; }));

  return {
    eligible: eligible.length,
    attached: attached.length,
    rate: eligible.length ? Math.round(attached.length / eligible.length * 100) : null,
    rev: revenue,
    bundled: pool.filter(x => isBundled(S, x.r)).length,
    top: Object.entries(count).sort((a, b) => b[1] - a[1])
  };
}

/* ------------------------------------------------------------- amounts */

/**
 * What one billing period costs, derived from the plan and add-ons.
 * Annual gets the full discount; half-yearly gets half of it; quarterly none.
 */
export function planAmount(S, r) {
  const base = priceOf(S, r.plan) + addonMonthly(S, r);
  if (!base) return 0;

  const cycle = String(r.cycle || '').toLowerCase();
  const discount = (S.annualDiscount || 0) / 100;

  if (cycle === 'annual') return Math.round(base * 12 * (1 - discount));
  if (cycle === 'half-yearly') return Math.round(base * 6 * (1 - discount / 2));
  if (cycle === 'quarterly') return Math.round(base * 3);
  return base;
}

/** The amount actually recorded on the row, or null when there is none. */
export function statedAmount(r) {
  const a = parseFloat(String(r.amount == null ? '' : r.amount).replace(/[^0-9.]/g, ''));
  return Number.isFinite(a) && a > 0 ? a : null;
}

/**
 * Is the amount a fact rather than an estimate?
 *
 * With the clients app connected it owns the account facts, and it sends an
 * amount only for accounts that actually pay — so a blank one means "this
 * account pays nothing", not "look up the list price". Substituting
 * planAmount() there printed a plan's list price against free and demo
 * accounts as though it were revenue. Without that connector nothing else
 * supplies an amount (the offline sample rows are all seeded blank), so the
 * plan price stays the best estimate available.
 */
export const amountKnown = (S, r) =>
  statedAmount(r) !== null || !S.clientsApp;

/** An explicit amount on the record wins; otherwise fall back to the plan. */
export function amountOf(S, r) {
  const stated = statedAmount(r);
  if (stated !== null) return stated;
  return S.clientsApp ? 0 : planAmount(S, r);
}

/** A client only recurs if it has both a cycle and a priced plan. */
/*
 * Billed if there is a cycle and money, whatever the source of the money.
 * This used to ask priceOf(plan) > 0, which threw away the amount the
 * connector had actually sent whenever the plan was missing from the local
 * price table - a paying client valued at zero and hidden from Billing.
 */
export const isBilled = (S, r) => periodDays(r.cycle) > 0 && amountOf(S, r) > 0;

/* ----------------------------------------------------------------- MRR */

/**
 * How many months one charge covers.
 *
 * An unrecognised cycle counts as monthly, matching periodMonths() on the
 * server, whose final fallback is 1. Lifetime is 0: there is no recurring
 * month in it, and dividing by it would be a guess.
 *
 * Deals need this too — a deal's worth is its client's charge, and summing
 * one client's annual 950 with another's monthly 49 gave a total that meant
 * nothing. Both sides now divide by the same number.
 */
export const cycleMonths = c => {
  const s = String(c || '').toLowerCase();
  return s === 'lifetime' ? 0 : s === 'annual' ? 12 : s === 'half-yearly' ? 6 : s === 'quarterly' ? 3 : 1;
};

/** Normalise one client's charge to a monthly figure. */
export function mrrOf(S, r, d) {
  if (d.excluded || !d.live) return 0;

  const months = cycleMonths(r.cycle);
  if (!months) return 0;

  const amount = amountOf(S, r);
  if (!amount) return 0;

  return amount / months;
}

export const mrrTotal = (S, rows, derive) =>
  rows.reduce((t, r) => {
    const d = derive(r);
    return t + (d.paid ? mrrOf(S, r, d) : 0);
  }, 0);

/**
 * The same total, split by how the customer is billed.
 *
 * One MRR figure hides the shape of the book: AED 818 reads the same whether
 * it is nine monthly subscriptions or four annual invoices, and those are very
 * different things to collect. `monthly` and `annual` are both monthly-rate,
 * so they still add up to mrrTotal; `annualBilled` is what the annual
 * customers actually hand over in a year.
 */
export function mrrSplit(S, rows, derive) {
  const out = { monthly: 0, annual: 0, monthlyCount: 0, annualCount: 0, annualBilled: 0 };

  rows.forEach(r => {
    const d = derive(r);
    if (!d.paid) return;
    const m = mrrOf(S, r, d);
    if (!m) return;

    if (String(r.cycle || '').toLowerCase() === 'monthly') {
      out.monthly += m;
      out.monthlyCount += 1;
    } else {
      // Anything not billed monthly is a longer cycle: annual, half-yearly…
      out.annual += m;
      out.annualCount += 1;
      out.annualBilled += amountOf(S, r);
    }
  });

  return out;
}

/** Revenue attached to any client currently carrying a money-losing flag. */
const AT_RISK_TAGS = ['unpaid', 'expired', 'renewing', 'trial-over', 'stale'];

export const mrrAtRisk = (S, rows, derive) =>
  rows.reduce((t, r) => {
    const d = derive(r);
    return t + (d.flags.some(f => AT_RISK_TAGS.includes(f.tag)) ? mrrOf(S, r, d) : 0);
  }, 0);
